import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isNative } from "@/lib/native";
import { X } from "@phosphor-icons/react";

const STORAGE_KEY = "pronto_cookie_consent";

/**
 * Minimal GDPR cookie banner — Pronto usa SOLO cookie tecnici (sessione login +
 * questa preferenza). Quindi niente consenso è tecnicamente richiesto, ma un
 * disclaimer trasparente crea fiducia.
 *
 * Comportamento:
 *   • Anonimi landing → banner sticky bottom, full-width SOTTILE (non copre CTA)
 *   • Autenticati → niente banner (hanno già accettato ToS al signup)
 *   • Una volta chiuso → mai più (localStorage)
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { user } = useAuth() || {};

  useEffect(() => {
    try {
      // iter v29 r195 — Su app native iOS/Android NON serve cookie banner
      // (è un'app, non un sito web; GDPR cookie banner non è applicabile).
      if (isNative) return;
      // Hide on internal screenshot/social-share pages — these are rendered
      // by Playwright to produce 1080×1080 marketing assets and the banner
      // would pollute the bottom of the image.
      if (typeof window !== "undefined" &&
          (window.location.pathname.startsWith("/og/") ||
           window.location.pathname.startsWith("/share/") ||
           window.location.pathname.startsWith("/onboarding"))) {
        return;
      }
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch { /* SSR / private mode */ }
  }, []);

  // Se l'utente è loggato ha già accettato i ToS al signup → niente banner
  // (e auto-memorizza il consent così non ricompare mai)
  useEffect(() => {
    if (user) {
      try { localStorage.setItem(STORAGE_KEY, "accepted-auth"); } catch { /* noop */ }
      setVisible(false);
    }
  }, [user]);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-white/95 backdrop-blur-sm border-t border-[#E5E7E0] shadow-[0_-2px_10px_-4px_rgba(0,0,0,0.08)]"
      role="dialog"
      aria-label="Informativa cookie"
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <p className="flex-1 text-[12px] sm:text-[13px] text-[#3F3D38] leading-snug">
          <span className="hidden sm:inline">🍪 </span>
          Pronto usa <strong className="text-[#1C1C1A]">solo cookie tecnici</strong> per tenerti collegato. Niente tracker.{" "}
          <Link to="/terms?tab=cookie" className="underline text-[#7C8B6E] hover:opacity-80">Cookie policy</Link>
        </p>
        <button
          onClick={accept}
          className="shrink-0 bg-[#7C8B6E] hover:bg-[#6E7C61] text-white text-[12px] sm:text-[13px] font-medium px-3 sm:px-4 py-1.5 rounded-sm transition-colors"
          data-testid="cookie-accept-btn"
        >
          Ho capito
        </button>
        <button
          onClick={accept}
          aria-label="Chiudi"
          className="shrink-0 p-1 text-[#9A968B] hover:text-[#1C1C1A] transition-colors"
          data-testid="cookie-dismiss-btn"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
